import { IndirectTaxRegion, IndirectTaxRegionHelper } from '../../common';
import { IndirectTaxFilingsHelper } from '../helpers';
import { IIndirectTaxFilingDetail } from '../types';

import { useIndirectTaxFilingAggregates } from './useIndirectTaxFilingAggregates';

interface IndirectTaxFilingDetailVatReturnSummaryProps {
  filing: IIndirectTaxFilingDetail;
  region: IndirectTaxRegion;
  corporationId: number;
}

// Contribution-region return summary — transaction-derived figures for the
// filing period, sourced from the transaction aggregate endpoint. The box
// breakdown lives separately in the "Return boxes" panel below this one.
const IndirectTaxFilingDetailVatReturnSummary = ({
  filing,
  region,
  corporationId,
}: IndirectTaxFilingDetailVatReturnSummaryProps) => {
  const { aggregates, isLoading } = useIndirectTaxFilingAggregates({
    filing,
    region,
    corporationId,
  });

  const { currency } = filing;
  const taxLabel = IndirectTaxRegionHelper.isVatRegion(region) ? 'VAT' : 'tax';

  // Monetary rows, in the order requested for the return summary. Taxable
  // sales/purchases are pre-tax subtotal sums; gross are tax-inclusive totals.
  const amountRows: { label: string; value: number }[] = [
    { label: 'Taxable sales', value: aggregates.totalSubtotalSales ?? 0 },
    { label: `Output ${taxLabel}`, value: aggregates.totalTaxCollected },
    { label: 'Gross sales', value: aggregates.totalSales },
    {
      label: 'Taxable purchases',
      value: aggregates.totalSubtotalPurchases ?? 0,
    },
    { label: `Input ${taxLabel}`, value: aggregates.totalTaxPaid },
    { label: 'Gross purchases', value: aggregates.totalPurchases },
  ];

  return (
    <div className="flex flex-col">
      <span className="txt-medium-bold text-text-primary mb-3xl">
        Return summary
      </span>

      <div className="flex flex-col">
        <div className="flex items-center justify-between border-b pb-md mb-md">
          <span className="txt-extra-small-medium text-text-tertiary">
            Breakdown
          </span>
          <span className="txt-extra-small-medium text-text-tertiary">
            {currency ? `Amount (${currency})` : 'Amount'}
          </span>
        </div>

        <div className="flex flex-col gap-md">
          {/* Transaction count — an integer, not a currency amount. */}
          <div className="flex items-center justify-between">
            <span className="txt-small-regular text-text-secondary">
              Number of transactions
            </span>
            <span className="txt-small-medium text-text-primary">
              {isLoading
                ? '—'
                : aggregates.totalTransactions.toLocaleString('en-US')}
            </span>
          </div>

          {amountRows.map((row) => (
            <div key={row.label} className="flex items-center justify-between">
              <span className="txt-small-regular text-text-secondary">
                {row.label}
              </span>
              <span className="txt-small-medium text-text-primary">
                {!currency
                  ? 'N/A'
                  : isLoading
                    ? '—'
                    : IndirectTaxFilingsHelper.formatCurrency(row.value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export { IndirectTaxFilingDetailVatReturnSummary };
